import React, { useId, useEffect, useRef, useState } from 'react';
import { cx } from '@twind/core';
import OstroChatAvatar from 'app/components/chatbot/OstroChatAvatar';
import { MessageReadMarker } from 'app/modules/live-chat/components/MessageReadMarker';

export default function BaseChatItem({
  children,
  className,
  showAvatar = true,
  avatarURL,
  messageIndex = 0,
  messageId = '',
  isUserMessage = false,
  hideMessageOwnership = false,
}: {
  children: React.ReactNode;
  className?: string;
  showAvatar?: boolean;
  avatarURL?: string;
  messageIndex?: number;
  messageId?: string;
  isUserMessage?: boolean;
  hideMessageOwnership?: boolean;
}) {
  const reactId = useId();
  const uniqueId = messageId || `message-${messageIndex}-${reactId}`;
  const messageTypeId = `message-type-${uniqueId}`;
  const textContentId = `message-text-${uniqueId}`;

  const contentRef = useRef<HTMLDivElement>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [hasInteractiveContent, setHasInteractiveContent] = useState<boolean>(false);

  useEffect(() => {
    if (contentRef.current) {
      // Check if content has interactive elements that users should interact with
      const interactiveElements = contentRef.current.querySelectorAll(
        'button:not([aria-hidden="true"]), input:not([type="hidden"]):not([aria-hidden="true"]), select:not([aria-hidden="true"]), textarea:not([aria-hidden="true"]), [role="button"]:not([aria-hidden="true"]), [tabindex="0"]:not([aria-hidden="true"])'
      );

      const hasInteractive = interactiveElements.length > 0;
      setHasInteractiveContent(hasInteractive);

      if (!hasInteractive) {
        const textContent = contentRef.current.textContent || contentRef.current.innerText || '';
        setExtractedText(textContent.trim());
      } else {
        setExtractedText('');
      }
    }
  }, [children]);

  const getMessageTypeLabel = () => {
    if (isUserMessage) {
      return 'Your message:';
    }

    if (hasInteractiveContent) {
      return 'Assistant message with options. Tab to navigate choices:';
    }

    return 'Assistant message:';
  };

  return (
    <>
      {!hideMessageOwnership && (
        <span
          id={messageTypeId}
          className="sr-only"
        >
          {getMessageTypeLabel()}
        </span>
      )}
      <div
        data-message-index={messageIndex}
        data-testid="base-chat-item"
        role="listitem"
        aria-labelledby={hasInteractiveContent ? messageTypeId : `${messageTypeId} ${textContentId}`}
        data-message-id={messageId}
        className={cx(
          'ostro-chat-item relative scroll-mb-6 rounded-lg',
          '~(mb-4 flex)',
          className
        )}
        tabIndex={0}
      >
        <div className={`mr-3 mb-3 basis-6 w-[24px] ${showAvatar ? 'h-[24px]' : 'h-[12px]'}`}>
          {showAvatar && <OstroChatAvatar src={avatarURL} />}
        </div>
        <div className="flex-1">
          {!hasInteractiveContent && extractedText && (
            <span
              id={textContentId}
              className="sr-only"
            >
              {extractedText}
            </span>
          )}
          <div
            ref={contentRef}
            data-message-content
            aria-hidden={!hasInteractiveContent}
          >
            {children}
          </div>
        </div>
        {messageIndex ? <MessageReadMarker index={messageIndex} /> : null}
      </div>
    </>
  );
}
